import { CSSObject } from "@emotion/react";
import { constants, typographyStyles } from "@newsela/angelou";

const { colors } = constants;
const white = colors.ui.white[500];
const black = colors.ui.black[500];
const uiNeutral25 = colors.ui.neutral[25];
const uiNeutral500 = colors.ui.neutral[500];
const uiNeutral100 = colors.ui.neutral[100];
const uiCore500 = colors.ui.core[500];

const { fontFamily, textXSObj, textSObj } = typographyStyles;

export const cardContainerCss: CSSObject = {
  position: "relative",
  width: "min(100%, 440px)",
  minWidth: "288px",
  borderRadius: 12,
  backgroundColor: white,
  overflow: "hidden",
  ...fontFamily.circular,
  display: "flex",
  flexDirection: "column",
};

export const anchorTagCss: CSSObject = {
  position: "absolute",
  inset: 0,
  borderRadius: "inherit",
  zIndex: 1,
  textDecoration: "none",
  outline: "none",

  ":focus": {
    border: `2px solid ${uiCore500}`,
  },
};

/**
 * 16:9 box that changes layout by count:
 * 1 → single cell
 * 2 → 2 equal vertical columns
 * 3 → 3 equal vertical columns
 * 4 → 2x2 grid
 */
export const imageArea = (count: number): CSSObject => ({
  position: "relative",
  aspectRatio: "16 / 9",
  background: white,
  overflow: "hidden",
  display: "grid",
  gap: 0,
  alignItems: "stretch",
  justifyItems: "stretch",
  gridTemplateColumns:
    count === 4
      ? "repeat(2, minmax(0, 1fr))"
      : `repeat(${Math.max(count, 1)}, minmax(0, 1fr))`,
  gridTemplateRows: count === 4 ? "repeat(2, minmax(0, 1fr))" : "1fr",
});

export const imageCellCss: CSSObject = {
  position: "relative",
  width: "100%",
  height: "100%",
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
};

export const imgCss: CSSObject = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

export const imagePlaceholderCss: CSSObject = {
  width: "100%",
  height: "100%",
  background: uiNeutral25,
};

export const metadataContainerCss: CSSObject = {
  display: "flex",
  justifyContent: "space-between",
  padding: "16px 16px 8px 16px",
  alignItems: "center",
};

export const displayDateCss: CSSObject = {
  ...textXSObj,
};

export const featureTagCss = (backgroundColor: string): CSSObject => ({
  fontSize: "11px",
  backgroundColor: backgroundColor || black,
  color: white,
  padding: "0 4px",
  borderRadius: "2px",
  alignContent: "center",
  textTransform: "uppercase",
  fontWeight: 700,
  lineHeight: "133%",
});

export const titleLineClampCss: CSSObject = {
  margin: 0,
  padding: "0 16px",
};

export const detailsContainerCss: CSSObject = {
  ...textXSObj,
  color: uiNeutral500,
  padding: "8px 16px 0px 16px",
};

export const bookmarkButtonContainerCss: CSSObject = {
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 3, // > link (1), > bottom overlay (2)
  pointerEvents: "auto",
  display: "grid",
};

export const bookmarkButtonCss: CSSObject = {
  width: 32,
  height: 32,
  borderRadius: 999,
  cursor: "pointer",
  outline: "none",
  display: "flex",
  justifyContent: "center",
  backgroundColor: uiNeutral25,

  ":focus-visible": {
    border: `2px solid ${uiCore500}`,
    background: uiNeutral100,
  },

  ":hover": {
    background: uiNeutral100,
  },
};

export const badgeContainerCss: CSSObject = {
  position: "absolute",
  top: 8,
  left: 8,
  right: 56,
  zIndex: 2, // above stretched Link (1), below bookmark (3)
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  margin: 0,
  padding: 0,
  listStyle: "none",

  pointerEvents: "none",
  "& > li": {
    pointerEvents: "auto",
  },
};

export const overlayTagContainerCss: CSSObject = {
  position: "absolute",
  left: 8,
  right: 8,
  bottom: 8,
  zIndex: 2, // Above the stretched Link (z-index: 1)
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  pointerEvents: "none",
};

const overlayPillBase: CSSObject = {
  ...textXSObj,
  padding: "2px 8px",
  borderRadius: "2px",
  height: "28px",
  backgroundColor: black,
  color: white,
  fontWeight: 400,
  display: "flex",
  alignItems: "center",
};

export const leftTagCss: CSSObject = {
  ...overlayPillBase,
  ...textSObj,
  padding: "4px 8px",
};

export const rightTagCss: CSSObject = {
  ...overlayPillBase,
  gap: 6,

  // Nested SVG styles
  svg: {
    width: "12px",
    stroke: white,
    path: { fill: white },
  },
};

export const specialContentCss: CSSObject = {
  padding: "0 16px 16px 16px",
  marginTop: "auto",
};
